/**
 * Per-role KPI (`/v2/stats/kpi/me|trainer|org`). Default window (no from/to)
 * is computed by BE as the last 30 days in the caller's timezone.
 * `trainer` requires `stats_clients_read`, `org` requires `stats_org_read` + OWNER.
 */
import type {
  FetchKpiMeInOrganizationArgs,
  ApiKpiMeInOrganization,
  ApiKpiByOrganization,
  FetchKpiTrainerArgs,
  FetchKpiOrgArgs,
  StatsRangeArgs,
  ApiKpiTrainer,
  ApiKpiOrg,
  ApiKpiMe,
} from './types/stats.types';

import { get } from './axios';

// WS3 — `organizationIds` (uuid ids + literal 'personal') scopes the
// schedule-derived KPIs (training/meals) only; measurements stay global.
export type FetchKpiMeArgs = StatsRangeArgs & { organizationIds?: string[] };

export const fetchKpiMe = async (args: FetchKpiMeArgs = {}) => {
  const response = await get<ApiKpiMe>('/v2/stats/kpi/me', {
    query: {
      from: args.from,
      to: args.to,
      ...(args.organizationIds?.length
        ? { organizationIds: args.organizationIds.join(',') }
        : {}),
    },
  });
  return response ?? null;
};

export const fetchKpiMeByOrganization = async () => {
  const response = await get<ApiKpiByOrganization>(
    '/v2/stats/kpi/me/by-organization',
  );
  return response ?? null;
};

export const fetchKpiMeInOrganization = async (
  args: FetchKpiMeInOrganizationArgs,
) => {
  const response = await get<ApiKpiMeInOrganization>(
    '/v2/stats/kpi/me/organization',
    {
      query: {
        from: args.from,
        organizationId: args.organizationId,
        to: args.to,
      },
    },
  );
  return response ?? null;
};

export const fetchKpiTrainer = async (args: FetchKpiTrainerArgs = {}) => {
  const response = await get<ApiKpiTrainer>('/v2/stats/kpi/trainer', {
    query: {
      clientId: args.clientId,
      from: args.from,
      to: args.to,
      organizationId: args.organizationId,
    },
  });
  return response ?? null;
};

export const fetchKpiOrg = async (args: FetchKpiOrgArgs) => {
  const response = await get<ApiKpiOrg>('/v2/stats/kpi/org', {
    query: {
      from: args.from,
      groupBy: args.groupBy,
      organizationId: args.organizationId,
      to: args.to,
    },
  });
  return response ?? null;
};
